import React from 'react'
import { useTranslation } from 'react-i18next'
import { useOutletContext } from 'react-router-dom'
import { Controller, useFormContext } from 'react-hook-form'
import { Box, Button, FormHelperText, MenuItem, Select, Stack, Typography } from '@mui/material'

import { useUser } from 'store'
import TextField from 'components/global/CUK/TextField'
import { FormLabel } from 'pages/Ran/Form/FormStyles'
import { canEditStimeFields, canSeeStimeFields } from 'pages/Ran/permissions'
import { generatePassphrase } from 'utils/generate-passphrase'

export const findSsid = (ssids, ssidId) => ssids.find(({ id }) => String(id) === String(ssidId))

export const useStimeContext = () => {
  const [user] = useUser()
  const { isStime, ssids, metkVlans, byodVlans } = useOutletContext() ?? {}
  const availableSsids = ssids ?? []
  const vlansByStimeType = { byod: byodVlans ?? [], metk: metkVlans ?? [] }

  const getVlanOptions = ssidId => vlansByStimeType[findSsid(availableSsids, ssidId)?.stime_type] ?? []

  return {
    isVisible: Boolean(isStime) && canSeeStimeFields(user),
    isEditable: canEditStimeFields(user),
    ssids: availableSsids,
    getVlanOptions
  }
}

export const isByodSsid = (ssids, ssidId) => findSsid(ssids, ssidId)?.stime_type === 'byod'

const getDefaultVlan = ({ vlanOptions, currentVlan, isByod }) => {
  if (vlanOptions.includes(currentVlan)) {
    return currentVlan
  }
  if (isByod || vlanOptions.length === 1) {
    return vlanOptions[0] ?? ''
  }
  return ''
}

export const getStimeDefaultValues = (room, { ssids, getVlanOptions }) => {
  const ssid = findSsid(ssids, room.ssid_id)

  return {
    ssid_id: ssid ? String(ssid.id) : '',
    vlan: ssid
      ? getDefaultVlan({
          vlanOptions: getVlanOptions(room.ssid_id),
          currentVlan: room.vlan,
          isByod: isByodSsid(ssids, room.ssid_id)
        })
      : '',
    wifi_psk: room.wifi_psk ?? ''
  }
}

export const buildPreSharedKeys = ({ ssid_id, vlan, wifi_psk }) => ({
  ssid_id,
  vlan: Number(vlan),
  ...(wifi_psk && { passphrase: wifi_psk })
})

const getSelectSx = hasError => ({
  height: '40px',
  borderRadius: '8px',
  fontSize: 12,
  width: '100%',
  backgroundColor: 'white',
  '& .MuiSelect-select': {
    fontSize: 12,
    display: 'flex',
    alignItems: 'center',
    paddingLeft: '12px',
    height: '40px'
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: hasError ? 'error.main' : '#EFEFEF',
    borderRadius: '8px'
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: hasError ? 'error.main' : '#EFEFEF'
  },
  '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
    borderColor: hasError ? 'error.main' : '#EFEFEF'
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: hasError ? 'error.main' : '#2570EB'
  },
  '& > i': {
    color: '#464646'
  },
  '&.Mui-disabled': {
    backgroundColor: 'grey.100',
    '& .MuiSelect-select': {
      color: 'grey.500',
      WebkitTextFillColor: 'unset'
    },
    '& > i': {
      color: 'grey.500'
    }
  }
})

const createSelectIcon = iconClassName => props => (
  <i
    {...props}
    className={iconClassName}
    aria-hidden="true"
    style={{ fontSize: 12, marginRight: 12, pointerEvents: 'none' }}
  />
)

const SelectChevron = createSelectIcon('fa-regular fa-chevron-down')
const SelectLock = createSelectIcon('fa-regular fa-lock')

export const getSsidOptions = ssids => ssids.map(({ id, name }) => ({ value: String(id), label: name }))

export const getSsidPlaceholder = (t, ssidOptions) => {
  if (ssidOptions.length === 0) {
    return t('ran.form.no_ssid', 'Aucun SSID disponible')
  }
  return t('ran.form.ssid_placeholder', 'Sélectionnez un SSID')
}

export const FormSelect = ({
  id,
  label,
  value,
  options,
  placeholder,
  emptyOptionsLabel,
  isLocked = false,
  disabled,
  errorLabel,
  onChange,
  onBlur
}) => {
  const hasError = Boolean(errorLabel)
  const selectedOption = options.find(option => String(option.value) === String(value))

  const handleChange = event => onChange(event.target.value)

  const renderValue = () => {
    if (!selectedOption) {
      return (
        <Typography component="span" sx={{ fontSize: 12, color: 'grey.500' }}>
          {placeholder}
        </Typography>
      )
    }
    return selectedOption.label
  }

  return (
    <Box>
      <FormLabel id={`${id}-label`}>{label}</FormLabel>
      <Select
        labelId={`${id}-label`}
        value={selectedOption ? selectedOption.value : ''}
        onChange={handleChange}
        onBlur={onBlur}
        disabled={disabled}
        error={hasError}
        displayEmpty
        size="small"
        renderValue={renderValue}
        IconComponent={isLocked ? SelectLock : SelectChevron}
        sx={getSelectSx(hasError)}>
        {options.length === 0 && emptyOptionsLabel && (
          <MenuItem value="" disabled sx={{ fontSize: 12 }}>
            {emptyOptionsLabel}
          </MenuItem>
        )}
        {options.map(option => (
          <MenuItem key={option.value} value={option.value} sx={{ fontSize: 12 }}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {hasError && <FormHelperText error>{errorLabel}</FormHelperText>}
    </Box>
  )
}

const StimeFields = ({ ssids, getVlanOptions, disabled, isSsidLocked = false }) => {
  const { t } = useTranslation()
  const {
    control,
    getValues,
    setValue,
    watch,
    formState: { errors }
  } = useFormContext()
  const selectedSsidId = watch('ssid_id')

  const ssidOptions = getSsidOptions(ssids)
  const vlanOptions = getVlanOptions(selectedSsidId).map(vlan => ({ value: vlan, label: String(vlan) }))
  const isByod = isByodSsid(ssids, selectedSsidId)

  const handleSsidChange = onChange => ssidId => {
    onChange(ssidId)
    const nextVlan = getDefaultVlan({
      vlanOptions: getVlanOptions(ssidId),
      currentVlan: getValues('vlan'),
      isByod: isByodSsid(ssids, ssidId)
    })
    setValue('vlan', nextVlan, { shouldValidate: nextVlan !== '' })
  }

  const handleGeneratePassphrase = () => {
    setValue('wifi_psk', generatePassphrase(), { shouldDirty: true, shouldValidate: true })
  }

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Controller
            control={control}
            name="ssid_id"
            render={({ field: { value, onChange, onBlur } }) => (
              <FormSelect
                id="ran-form-ssid"
                label={t('ran.form.ssid', 'SSID')}
                value={value}
                options={ssidOptions}
                placeholder={getSsidPlaceholder(t, ssidOptions)}
                disabled={disabled || isSsidLocked || ssidOptions.length === 0}
                errorLabel={errors.ssid_id?.message}
                onChange={handleSsidChange(onChange)}
                onBlur={onBlur}
              />
            )}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Controller
            control={control}
            name="vlan"
            render={({ field: { value, onChange, onBlur } }) => (
              <FormSelect
                id="ran-form-vlan"
                label={t('ran.form.vlan', 'VLAN')}
                value={value}
                options={vlanOptions}
                placeholder={t('ran.form.vlan_placeholder', 'Sélectionnez un VLAN')}
                emptyOptionsLabel={t('ran.form.vlan_select_ssid_first', 'Sélectionnez d’abord un SSID')}
                isLocked={isByod}
                disabled={disabled || isByod}
                errorLabel={errors.vlan?.message}
                onChange={onChange}
                onBlur={selectedSsidId ? onBlur : undefined}
              />
            )}
          />
        </Box>
      </Stack>
      <Box>
        <FormLabel>{t('ran.form.passphrase', 'Passphrase')}</FormLabel>
        <Controller
          control={control}
          name="wifi_psk"
          render={({ field: { value, onChange, ref } }) => (
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                value={value}
                setvalue={onChange}
                disabled={disabled}
                fullWidth
                input={{
                  inputRef: ref,
                  placeholder: t('ran.form.passphrase_placeholder', 'Générée automatiquement si vide'),
                  inputProps: { 'aria-label': t('ran.form.passphrase', 'Passphrase') }
                }}
                footer={{
                  errorLabel: errors.wifi_psk?.message
                }}
                error={Boolean(errors.wifi_psk)}
              />
              {!disabled && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleGeneratePassphrase}
                  aria-label={t('ran.form.generate_passphrase', 'Générer une passphrase')}
                  startIcon={<i className="fa-regular fa-arrows-rotate" />}
                  sx={{
                    flexShrink: 0,
                    '& .MuiButton-startIcon > *:nth-of-type(1)': { fontSize: 12 }
                  }}>
                  {t('ran.form.generate', 'Générer')}
                </Button>
              )}
            </Stack>
          )}
        />
      </Box>
    </>
  )
}

export default StimeFields
